import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'
import { useLocation } from 'react-router-dom'
import { isRecord } from 'lib/object/objectUtils'
import type { MarkerSelectionData } from 'tripPlanner/components/Chat/chatMapConstants'

export type RightPanelView = 'map' | 'timeline' | 'list' | 'cart'

const DEFAULT_RIGHT_PANEL_VIEW: RightPanelView = 'map'
const CHECKOUT_RETURN_VIEW_HISTORY_STATE_KEY = 'tripChatRightPanelView'

interface UseRightPanelStateReturn {
  rightPanelView: RightPanelView
  setRightPanelView: Dispatch<SetStateAction<RightPanelView>>
  selectedDayItineraryId: number | undefined
  setSelectedDayItineraryId: Dispatch<SetStateAction<number | undefined>>
  selectedMarkerData: MarkerSelectionData | undefined
  handleMarkerSelect: (data: MarkerSelectionData | undefined) => void
  handleMarkerOverlayClose: () => void
}

function isStoredRightPanelView(value: unknown): value is RightPanelView {
  return value === 'map' || value === 'timeline' || value === 'list' || value === 'cart'
}

export function getTripChatCheckoutReturnView(): RightPanelView | undefined {
  if (typeof window === 'undefined') return undefined
  const historyState = window.history.state
  if (!isRecord(historyState)) return undefined
  const value = historyState[CHECKOUT_RETURN_VIEW_HISTORY_STATE_KEY]
  return isStoredRightPanelView(value) ? value : undefined
}

function resolveRightPanelViewAction(
  action: SetStateAction<RightPanelView>,
  currentView: RightPanelView,
): RightPanelView {
  return typeof action === 'function' ? action(currentView) : action
}

export function rememberTripChatCheckoutReturnView(view: RightPanelView) {
  if (isStoredRightPanelView(view) && typeof window !== 'undefined') {
    const historyState = window.history.state
    window.history.replaceState({
      ...(isRecord(historyState) ? historyState : {}),
      [CHECKOUT_RETURN_VIEW_HISTORY_STATE_KEY]: view,
    }, '', window.location.href)
  }
}

export function useRightPanelState(): UseRightPanelStateReturn {
  const { search } = useLocation()
  const [rightPanelView, setRightPanelViewState] = useState<RightPanelView>(() => {
    if (new URLSearchParams(search).get('openCart') === 'true') {
      return 'cart'
    }
    return getTripChatCheckoutReturnView() ?? DEFAULT_RIGHT_PANEL_VIEW
  })
  const [selectedDayItineraryId, setSelectedDayItineraryId] = useState<number | undefined>(undefined)
  const [selectedMarkerData, setSelectedMarkerData] = useState<MarkerSelectionData | undefined>(undefined)

  const setRightPanelView = useCallback<Dispatch<SetStateAction<RightPanelView>>>((nextView) => {
    setRightPanelViewState(currentView => resolveRightPanelViewAction(nextView, currentView))
  }, [])

  const handleMarkerSelect = useCallback((data: MarkerSelectionData | undefined) => {
    setSelectedMarkerData(data)
  }, [])

  const handleMarkerOverlayClose = useCallback(() => {
    setSelectedMarkerData(undefined)
  }, [])

  return {
    rightPanelView,
    setRightPanelView,
    selectedDayItineraryId,
    setSelectedDayItineraryId,
    selectedMarkerData,
    handleMarkerSelect,
    handleMarkerOverlayClose,
  }
}
